import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Lock,
  Mail,
  User,
  Search,
  Plus,
  Loader2,
  CheckCircle,
  AlertCircle,
  X,
  Eye,
  EyeOff,
  Shield,
  Users,
  ChevronRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { API_BASE_URL as API_BASE } from '../../config/apiConfig';
import UserAvatar from '../Common/UserAvatar';

const UserSettings = () => {
  const { user } = useSelector((state) => state.user);
  const [activeTab, setActiveTab] = useState('password');
  const [isLoading, setIsLoading] = useState(false);

  // Change Password State
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Add User State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    email: '',
    full_name: ''
  });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState('');

  // Search for users
  const handleSearchUsers = async (query) => {
    setSearchQuery(query);
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}/auth/users/search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.data || []);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    } finally {
      setSearchLoading(false);
    }
  };

  // Change password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validate form
    if (!passwordForm.current_password || !passwordForm.new_password) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });

      if (response.ok) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordForm({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        toast.success('Password updated');
        setTimeout(() => setPasswordSuccess(''), 3000);
      } else {
        const error = await response.json();
        setPasswordError(error.detail || 'Failed to change password');
        toast.error(error.detail || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError('An error occurred while changing password');
      toast.error('An error occurred while changing password');
    } finally {
      setIsLoading(false);
    }
  };

  // Add new user
  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserError('');

    if (!addUserForm.email) {
      setAddUserError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(addUserForm.email)) {
      setAddUserError('Please enter a valid email address');
      return;
    }

    setAddUserLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}/auth/admin/users/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: addUserForm.email,
          full_name: addUserForm.full_name || undefined
        })
      });

      if (response.ok) {
        await response.json();
        toast.success(`User ${addUserForm.email} created successfully! Credentials sent to email.`);
        setAddUserForm({ email: '', full_name: '' });
        setShowAddModal(false);
        setSearchQuery('');
        setSearchResults([]);
      } else {
        const error = await response.json();
        setAddUserError(error.detail || 'Failed to create user');
        toast.error(error.detail || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error adding user:', error);
      setAddUserError('An error occurred while adding user');
      toast.error('An error occurred while adding user');
    } finally {
      setAddUserLoading(false);
    }
  };

  return (
    <div className="relative min-h-[80vh] w-full mx-auto p-2 sm:p-6 lg:p-8">
      {/* Dynamic Background Blobs */}
      <div className="absolute top-0 -left-4 w-72 h-72 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
      <div className="absolute top-0 -right-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
      <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>

      <div className="relative z-10 flex flex-col lg:flex-row gap-8">
        {/* Creative Sidebar Navigation */}
        <aside className="w-full lg:w-72 flex-shrink-0">
          <div className="backdrop-blur-xl bg-white/40 border border-white/40 rounded-3xl p-6 shadow-2xl sticky top-8">
            <div className="mb-8">
              <h1 className="text-3xl font-black bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Settings
              </h1>
              <p className="text-slate-500 text-sm mt-1 font-medium italic">Configure your experience</p>
            </div>

            <nav className="space-y-3">
              <button
                onClick={() => setActiveTab('password')}
                className={`w-full flex items-center justify-between p-4 rounded-2xl transition-all duration-300 group ${
                  activeTab === 'password'
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200 scale-[1.02]'
                    : 'bg-white/50 text-slate-600 hover:bg-white/80 hover:text-indigo-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${activeTab === 'password' ? 'bg-white/20' : 'bg-slate-100 group-hover:bg-indigo-50 transition-colors'}`}>
                    <Shield className="w-5 h-5" />
                  </div>
                  <span className="font-bold tracking-tight">Security</span>
                </div>
                <ChevronRight className={`w-4 h-4 transition-transform ${activeTab === 'password' ? 'rotate-90' : 'group-hover:translate-x-1'}`} />
              </button>

              <button
                onClick={() => setActiveTab('users')}
                className={`w-full flex items-center justify-between p-4 rounded-2xl transition-all duration-300 group ${
                  activeTab === 'users'
                    ? 'bg-purple-600 text-white shadow-lg shadow-purple-200 scale-[1.02]'
                    : 'bg-white/50 text-slate-600 hover:bg-white/80 hover:text-purple-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${activeTab === 'users' ? 'bg-white/20' : 'bg-slate-100 group-hover:bg-purple-50 transition-colors'}`}>
                    <Users className="w-5 h-5" />
                  </div>
                  <span className="font-bold tracking-tight">Team</span>
                </div>
                <ChevronRight className={`w-4 h-4 transition-transform ${activeTab === 'users' ? 'rotate-90' : 'group-hover:translate-x-1'}`} />
              </button>
            </nav>

            <div className="mt-8 pt-6 border-t border-white/40">
              <div className="flex items-center gap-3 px-2">
                <UserAvatar 
                  user={user} 
                  size="md" 
                  indicator={true}
                  className="shadow-md"
                />
                <div className="overflow-hidden">
                  <p className="text-sm font-bold text-slate-900 truncate">{user?.full_name || 'User'}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Content Area */}
        <main className="flex-1 min-w-0">
          <AnimatePresence mode="wait">
            {activeTab === 'password' && (
              <motion.div
                key="password"
                initial={{ opacity: 0, x: 20, filter: 'blur(10px)' }}
                animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, x: -20, filter: 'blur(10px)' }}
                className="space-y-6"
              >
                <div className="backdrop-blur-xl bg-white/60 border border-white/60 p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden group">
                  <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity">
                    <Shield className="w-48 h-48 -mr-12 -mt-12 rotate-12" />
                  </div>

                  <div className="relative z-10">
                    <div className="mb-8">
                      <h2 className="text-2xl font-black text-slate-900 tracking-tight">Change Password</h2>
                      <p className="text-slate-500 mt-1 font-medium">Keep your account secure with a strong password</p>
                    </div>

                    <form onSubmit={handleChangePassword} className="space-y-6">
                      <div className="grid grid-cols-1 gap-6">
                        {/* Current Password */}
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-slate-700 ml-1">Current Password</label>
                          <div className="relative group/input">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within/input:text-indigo-500 transition-colors">
                              <Lock className="w-5 h-5" />
                            </div>
                            <input
                              type={showCurrentPassword ? "text" : "password"}
                              value={passwordForm.current_password}
                              onChange={(e) => setPasswordForm({
                                ...passwordForm,
                                current_password: e.target.value
                              })}
                              className="w-full pl-12 pr-12 py-4 bg-white/80 border-2 border-slate-100 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-medium placeholder:text-slate-300"
                              placeholder="••••••••"
                              disabled={isLoading}
                            />
                            <button
                              type="button"
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                            >
                              {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                        </div>

                        {/* New Password */}
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-slate-700 ml-1">New Password</label>
                          <div className="relative group/input">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within/input:text-indigo-500 transition-colors">
                              <Lock className="w-5 h-5" />
                            </div>
                            <input
                              type={showNewPassword ? "text" : "password"}
                              value={passwordForm.new_password}
                              onChange={(e) => setPasswordForm({
                                ...passwordForm,
                                new_password: e.target.value
                              })}
                              className="w-full pl-12 pr-12 py-4 bg-white/80 border-2 border-slate-100 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-medium placeholder:text-slate-300"
                              placeholder="••••••••"
                              disabled={isLoading}
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                            >
                              {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                          <p className="text-xs text-slate-400 font-semibold ml-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> Minimum 8 characters
                          </p>
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-slate-700 ml-1">Confirm New Password</label>
                          <div className="relative group/input">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within/input:text-indigo-500 transition-colors">
                              <Lock className="w-5 h-5" />
                            </div>
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              value={passwordForm.confirm_password}
                              onChange={(e) => setPasswordForm({
                                ...passwordForm,
                                confirm_password: e.target.value
                              })}
                              className="w-full pl-12 pr-12 py-4 bg-white/80 border-2 border-slate-100 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-medium placeholder:text-slate-300"
                              placeholder="••••••••"
                              disabled={isLoading}
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                            >
                              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Feedback Messages */}
                      <AnimatePresence>
                        {passwordError && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="flex items-center gap-3 p-4 bg-red-50/50 backdrop-blur-md border border-red-200 rounded-2xl text-red-600 text-sm font-bold shadow-sm shadow-red-100/50"
                          >
                            <AlertCircle className="w-5 h-5 flex-shrink-0" />
                            {passwordError}
                          </motion.div>
                        )}
                        {passwordSuccess && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="flex items-center gap-3 p-4 bg-green-50/50 backdrop-blur-md border border-green-200 rounded-2xl text-green-600 text-sm font-bold shadow-sm shadow-green-100/50"
                          >
                            <CheckCircle className="w-5 h-5 flex-shrink-0" />
                            {passwordSuccess}
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="group relative w-full overflow-hidden rounded-2xl bg-indigo-600 p-4 font-black text-white shadow-xl shadow-indigo-200 transition-all hover:bg-indigo-700 active:scale-[0.98] disabled:bg-slate-300 disabled:shadow-none"
                      >
                        <div className="absolute inset-x-0 bottom-0 h-1 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                        <span className="flex items-center justify-center gap-2">
                          {isLoading ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              Update Security Credentials
                              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </>
                          )}
                        </span>
                      </button>
                    </form>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'users' && (
              <motion.div
                key="users"
                initial={{ opacity: 0, x: 20, filter: 'blur(10px)' }}
                animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, x: -20, filter: 'blur(10px)' }}
                className="space-y-8"
              >
                {/* Team Management Card */}
                <div className="backdrop-blur-xl bg-white/60 border border-white/60 p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                    <div>
                      <h2 className="text-2xl font-black text-slate-900 tracking-tight">Team Management</h2>
                      <p className="text-slate-500 mt-1 font-medium">Control who has access to your analyzer</p>
                    </div>
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-purple-600 text-white rounded-2xl font-black shadow-xl shadow-purple-200 hover:bg-purple-700 transition-all active:scale-[0.98]"
                    >
                      <Plus className="w-5 h-5" />
                      Add Team Member
                    </button>
                  </div>

                  <div className="relative group/search">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within/search:text-purple-500 transition-colors" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearchUsers(e.target.value)}
                      placeholder="Find member by name or email..."
                      className="w-full pl-12 pr-4 py-4 bg-white/80 border-2 border-slate-100 rounded-2xl focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/10 transition-all font-medium placeholder:text-slate-300"
                    />
                  </div>

                  {/* Results Section */}
                  <div className="mt-8">
                    <AnimatePresence mode="popLayout">
                      {searchLoading ? (
                        <motion.div
                          key="loading"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="flex flex-col items-center justify-center py-12 text-slate-400"
                        >
                          <Loader2 className="w-10 h-10 animate-spin mb-4" />
                          <p className="font-bold">Searching the database...</p>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="search-content"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          {searchResults.length > 0 ? (
                            <motion.div
                              key="results"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="grid grid-cols-1 md:grid-cols-2 gap-4"
                            >
                              {searchResults.map((resultUser) => (
                                <motion.div
                                  layout
                                  key={resultUser.id}
                                  className="p-5 bg-white/80 border border-slate-100 rounded-3xl flex items-center gap-4 hover:shadow-xl hover:shadow-slate-100 transition-all group"
                                >
                                  <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-600 font-bold group-hover:bg-purple-600 group-hover:text-white transition-colors">
                                    {resultUser.full_name?.charAt(0) || 'U'}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-black text-slate-900 truncate">{resultUser.full_name || 'N/A'}</p>
                                    <p className="text-sm text-slate-500 font-medium truncate">{resultUser.email}</p>
                                  </div>
                                </motion.div>
                              ))}
                            </motion.div>
                          ) : (
                            searchQuery.length >= 2 && (
                              <motion.div
                                key="empty"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="p-12 text-center bg-slate-50/50 backdrop-blur-md rounded-[2.5rem] border border-dashed border-slate-200"
                              >
                                <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                                <h3 className="text-xl font-black text-slate-900 mb-2">Member Not Found</h3>
                                <p className="text-slate-500 font-medium max-w-sm mx-auto">
                                  We couldn't find anyone matching "{searchQuery}" in your current team database.
                                </p>
                              </motion.div>
                            )
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* Add User Modal - Innovative Design */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm"
              onClick={() => setShowAddModal(false)}
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-lg backdrop-blur-2xl bg-white border border-white/40 rounded-[3rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] p-8 sm:p-12 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Background Decor */}
              <div className="absolute top-0 right-0 p-12 opacity-[0.05]">
                <Plus className="w-64 h-64 -mr-32 -mt-32 rotate-45" />
              </div>

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-3xl font-black text-slate-900 tracking-tight">Onboard Member</h3>
                    <p className="text-slate-500 mt-1 font-medium italic">Grow your analytical team</p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="p-3 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-2xl transition-all hover:rotate-90"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleAddUser} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700 ml-1">Email Connection</label>
                    <div className="relative group/input">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within/input:text-purple-500 transition-colors" />
                      <input
                        type="email"
                        value={addUserForm.email}
                        onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
                        placeholder="collaborator@example.com"
                        className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:outline-none focus:border-purple-500 focus:bg-white transition-all font-medium"
                        disabled={addUserLoading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700 ml-1">Member Name (Optional)</label>
                    <div className="relative group/input">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within/input:text-purple-500 transition-colors" />
                      <input
                        type="text"
                        value={addUserForm.full_name}
                        onChange={(e) => setAddUserForm({ ...addUserForm, full_name: e.target.value })}
                        placeholder="John Smith"
                        className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:outline-none focus:border-purple-500 focus:bg-white transition-all font-medium"
                        disabled={addUserLoading}
                      />
                    </div>
                  </div>

                  <AnimatePresence>
                    {addUserError && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="flex items-center gap-3 p-4 bg-red-50 text-red-600 rounded-2xl text-sm font-bold"
                      >
                        <AlertCircle className="w-5 h-5" />
                        {addUserError}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100/50">
                    <p className="text-xs text-amber-700 font-bold leading-relaxed flex gap-2">
                      <Shield className="w-4 h-4 flex-shrink-0" />
                      Security: A strong temporary password will be auto-generated and sent securely to the member's email inbox immediately.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      disabled={addUserLoading}
                      className="flex-1 px-8 py-4 border-2 border-slate-100 text-slate-600 rounded-2xl font-black hover:bg-slate-50 transition-all disabled:opacity-50"
                    >
                      Wait, Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={addUserLoading}
                      className="flex-[1.5] flex items-center justify-center gap-2 px-8 py-4 bg-purple-600 text-white rounded-2xl font-black shadow-xl shadow-purple-200 hover:bg-purple-700 transition-all active:scale-[0.98] disabled:bg-slate-300"
                    >
                      {addUserLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Onboarding...
                        </>
                      ) : (
                        <>
                          Submit & Send Invite
                          <ChevronRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserSettings;
